import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, LogOut, Copy, CheckCheck, Loader2 } from 'lucide-react';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/**
 * MFA enrolment is mandatory for every user. If a user lands here with
 * MFA already enabled (e.g. someone navigates by URL) we bounce them home
 * immediately. Otherwise this page is the only route they can access
 * until they finish enrolment — enforced by the guard in App.js.
 */
export default function MFASetupPage() {
  const { user, updateUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState('start'); // 'start' | 'verify' | 'backup'
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [ackDownloaded, setAckDownloaded] = useState(false);

  useEffect(() => {
    if (user?.mfa_enabled) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const beginSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mfa/setup`);
      setSetupData(res.data);
      setStep('verify');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mfa/verify`, { code: verificationCode });
      setBackupCodes(res.data.backup_codes || []);
      setStep('backup');
      toast.success('MFA enabled successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const finish = () => {
    updateUser({ mfa_enabled: true });
    navigate('/', { replace: true });
  };

  const copyCode = (code, idx) => {
    navigator.clipboard.writeText(code);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1200);
  };

  const downloadBackupCodes = () => {
    const blob = new Blob(
      [
        `Cyber Forte — Backup Codes\n`,
        `Account: ${user?.email}\n`,
        `Generated: ${new Date().toISOString()}\n\n`,
        backupCodes.join('\n'),
        `\n\nStore these in a safe place. Each code works once.`,
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cyberforte-backup-codes.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setAckDownloaded(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-xl border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-teal-700" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Two-factor authentication required</h1>
            <p className="text-sm text-slate-500">Signed in as {user?.email}</p>
          </div>
        </div>

        {step === 'start' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Two-factor authentication is mandatory. You&apos;ll need an authenticator app
              such as <span className="font-medium">Google Authenticator</span>, <span className="font-medium">Microsoft Authenticator</span>, <span className="font-medium">1Password</span>,
              or <span className="font-medium">Authy</span> to continue.
            </p>
            <ol className="list-decimal list-inside text-sm text-slate-700 space-y-1 pl-1">
              <li>Install an authenticator app on your phone</li>
              <li>Scan the QR code we&apos;ll show next</li>
              <li>Enter the 6-digit code to finish enrolment</li>
            </ol>
            <div className="flex items-center justify-between pt-4 gap-2">
              <Button
                variant="outline"
                onClick={() => { logout(); navigate('/login', { replace: true }); }}
                data-testid="mfa-setup-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
              <Button onClick={beginSetup} disabled={loading} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="mfa-setup-start-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Start setup
              </Button>
            </div>
          </div>
        )}

        {step === 'verify' && setupData && (
          <div className="space-y-5">
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 flex flex-col items-center gap-3">
              <img
                src={setupData.qr_code}
                alt="MFA QR code"
                className="w-48 h-48 rounded bg-white p-2 border border-slate-200"
                data-testid="mfa-setup-qr"
              />
              <div className="text-xs text-slate-500 text-center">
                Can&apos;t scan? Enter this key manually:
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-slate-800 bg-white border border-slate-200 rounded px-2 py-1">
                  {setupData.secret}
                </code>
                <button
                  type="button"
                  onClick={() => copyCode(setupData.secret, 'secret')}
                  className="text-slate-500 hover:text-slate-800"
                  data-testid="mfa-setup-copy-secret"
                  aria-label="Copy secret"
                >
                  {copiedIdx === 'secret' ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="mfa-code" className="text-slate-900">Enter the 6-digit code from your app</Label>
              <Input
                id="mfa-code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                className="mt-2 tracking-widest font-mono text-lg text-center"
                data-testid="mfa-setup-code-input"
              />
            </div>

            <div className="flex items-center justify-between pt-2 gap-2">
              <Button variant="outline" onClick={() => setStep('start')} data-testid="mfa-setup-back-btn">
                Back
              </Button>
              <Button
                onClick={verifyAndEnable}
                disabled={loading || verificationCode.length !== 6}
                className="bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="mfa-setup-verify-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Verify and enable
              </Button>
            </div>
          </div>
        )}

        {step === 'backup' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              These are your one-time backup codes. Save them somewhere safe — each one lets you sign in if you lose your phone.
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="mfa-setup-backup-codes">
              {backupCodes.map((c, i) => (
                <div key={c} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-sm">
                  <span>{c}</span>
                  <button
                    type="button"
                    onClick={() => copyCode(c, i)}
                    className="text-slate-400 hover:text-slate-700"
                    aria-label="Copy backup code"
                  >
                    {copiedIdx === i ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-3 gap-2">
              <Button variant="outline" onClick={downloadBackupCodes} data-testid="mfa-setup-download-backup-btn">
                Download codes
              </Button>
              <Button
                onClick={finish}
                disabled={!ackDownloaded}
                className="bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="mfa-setup-finish-btn"
                title={ackDownloaded ? 'Continue to dashboard' : 'Download the backup codes first'}
              >
                Continue to dashboard
              </Button>
            </div>
            <p className="text-xs text-slate-500 pt-1">
              You must download your backup codes before you can continue. They&apos;re only shown once.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
